import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { NumericFormat } from 'react-number-format';
import { ClipLoader } from 'react-spinners';
import { getConfig } from './config/config';

function getExternalParams() {
    const params = new URLSearchParams(window.location.search);
    return {
        nit: params.get("nit") || "",
        cmpy: params.get("cmpy") || "",
        reg: params.get("reg") || "",
        teri: params.get("teri") || "",
        phone: params.get("phone") || "",
        referencia: params.get("referencia") || "",
        evt: params.get("evt") || "",
        cash_received: params.get("cash_received") || ""
    };
}

const WompiInterface = () => {
    const { apiBaseURL, publicKey, integritySecret } = getConfig();

    const [id, setId] = useState('');
    const [isIdLocked, setIsIdLocked] = useState(false);
    const [customerData, setCustomerData] = useState(null);
    const [payments, setPayments] = useState([]);
    const [selectedPayments, setSelectedPayments] = useState([]);
    const [error, setError] = useState('');
    const [notification, setNotification] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Modo de pago: "complete" o "partial"
    const [paymentOption, setPaymentOption] = useState('complete');

    // Monto total de abono (modo partial)
    const [partialPaymentAmount, setPartialPaymentAmount] = useState('');

    // Abonos asignados a cada factura
    const [appliedAmounts, setAppliedAmounts] = useState({});
    // Saldo pendiente de cada factura
    const [deductibles, setDeductibles] = useState({});

    useEffect(() => {
        const nitParam = new URLSearchParams(window.location.search).get('nit');
        if (nitParam && nitParam.trim() !== '') {
            const nit = nitParam.trim();
            setId(nit);
            handleValidateCustomer(nit);
        }
    }, []);

    // --- NOTIFICACIONES ---
    const showNotification = (message, type = 'error') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    // ----------------------------------------------------------------------
    // 1) VALIDAR CLIENTE
    // ----------------------------------------------------------------------
    const handleValidateCustomer = async (customId) => {
        const candidate = (typeof customId === 'string') ? customId : id;
        const targetId = (candidate || '').trim();
        if (!targetId) {
            const msg = 'Debes ingresar el documento.';
            setError(msg);
            showNotification(msg, 'error');
            return;
        }

        try {
            setError('');
            setIsLoading(true);
            setIsIdLocked(true);

            const validateResponse = await axios.get(
                `${apiBaseURL}/api/Validation/validate-customer/${encodeURIComponent(targetId)}`
            );
            if (!validateResponse.data?.message?.includes('existe')) {
                throw new Error('El cliente no existe en la base de datos.');
            }

            const { customerType } = validateResponse.data;

            const { data: customer } = await axios.get(
                `${apiBaseURL}/api/Validation/get-customer-details/${encodeURIComponent(targetId)}`
            );

            setCustomerData({
                id: customer.resal,
                name: customer.customerDetails.name,
                email: customer.customerDetails.email || '',
                phone: customer.customerDetails.telephone || '',
                type: customerType,
            });

            await handleFetchPayments(customer.resal);
            showNotification('Cliente validado exitosamente', 'success');
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Error al validar el cliente. Por favor intenta nuevamente.';
            setError(errorMessage);
            showNotification(errorMessage, 'error');
            setCustomerData(null);
            setPayments([]);
            setIsIdLocked(false);
        } finally {
            setIsLoading(false);
        }
    };


    // Cargar facturas pendientes
    const handleFetchPayments = async (customerId) => {
        try {
            setError('');
            const response = await axios.get(
                `${apiBaseURL}/api/Validation/customer-payments/${customerId}`
            );

            if (response.data.status === 'NotFound') {
                showNotification(response.data.message, 'info');
                setPayments([]);
                return;
            }

            const paymentsData = Array.isArray(response.data) ? response.data : [];

            // 1. comprobar en Wompi cuáles siguen PENDING
            const pendingMap = {};
            await Promise.all(
                paymentsData.map(async (p, idx) => {
                    if (p.transactionId) {
                        try {
                            const res = await axios.get(
                                `${apiBaseURL}/api/Validation/confirmation?transactionId=${p.transactionId}`
                            );
                            pendingMap[idx] = res.data.status === 'PENDING';
                        } catch {
                            pendingMap[idx] = false;
                        }
                    } else {
                        pendingMap[idx] = false;
                    }
                })
            );

            // filtrar solo las que NO están pendientes
            const visible = paymentsData.filter((_, idx) => !pendingMap[idx]);

            // if (paymentsData.length > 0) {
            if (visible.length > 0) {
                setPayments(
                    // paymentsData
                    visible
                        .map((p, index) => ({
                            id: String(index),
                            invoice: p.invoiceNumber,
                            invoiceValue: p.invoiceValue,
                            amount: p.netValue,
                            discount: p.discountResult,
                            finalAmount: p.netValueWithDiscount,
                            date: p.invoiceDate,
                            miscInfo: p.miscInfo,
                            transactionId: p.transactionId
                        }))
                );
            } else {
                showNotification('No se encontraron pagos para el cliente.', 'info');
                setPayments([]);
            }
        } catch (err) {
            const errorMessage =
                err.response?.data?.message ||
                'Error al obtener los pagos pendientes. Por favor intenta nuevamente.';
            setError(errorMessage);
            showNotification(errorMessage, 'error');
            setPayments([]);
        }
    };

    // ----------------------------------------------------------------------
    // 2) CÁLCULOS GENERALES
    // ----------------------------------------------------------------------
    // A) Suma total de las facturas (con descuento) - modo complete
    const calculateAllInvoicesTotal = () =>
        payments.reduce((sum, p) => sum + p.finalAmount, 0);

    // B) Monto total ingresado para abono
    const calculateAbonoAplicado = () => parseFloat(partialPaymentAmount) || 0;

    // C) Total a pagar en modo "complete" (solo facturas seleccionadas)
    const calculateTotalPagarComplete = () =>
        selectedPayments.reduce((total, pid) => {
            const payment = payments.find((p) => p.id === pid);
            return total + (payment ? payment.finalAmount : 0);
        }, 0);

    // D) Para modo manual, total de las facturas seleccionadas (para excedente)
    const calculateSelectedInvoicesTotal = () => {
        return selectedPayments.reduce((sum, pid) => {
            const pay = payments.find((p) => p.id === pid);
            return sum + (pay ? pay.finalAmount : 0);
        }, 0);
    };

    // E) Excedente (Saldo a Favor)
    const calculateExcess = () => {
        if (paymentOption === 'partial' && customerData?.type === 'M') {
            const diff = calculateAbonoAplicado() - calculateSelectedInvoicesTotal();
            return diff > 0 ? diff : 0;
        }
        const diff = calculateAbonoAplicado() - calculateAllInvoicesTotal();
        return diff > 0 ? diff : 0;
    };

    // F) Función para mostrar el Saldo Pendiente global (solo display)
    const calculatePendingBalanceOnlyForDisplay = () => {
        return payments.reduce((acc, pay) => {
            const { id, amount, finalAmount } = pay;
            const abonoReal = appliedAmounts[id] ?? 0;
            let leftover = 0;
            if (abonoReal >= finalAmount) {
                leftover = 0;
            } else {
                leftover = amount - abonoReal;
            }
            if (leftover < 0) leftover = 0;
            return acc + leftover;
        }, 0);
    };

    // ----------------------------------------------------------------------
    // 3) MODO “A” (Automático)
    // ----------------------------------------------------------------------
    useEffect(() => {
        if (
            customerData?.type === 'A' &&
            paymentOption === 'partial' &&
            partialPaymentAmount &&
            payments.length > 0
        ) {
            let remaining = parseFloat(partialPaymentAmount) || 0;
            const sorted = payments;

            const newDeductibles = {};
            const newApplied = {};
            const newSelected = [];

            for (const pay of sorted) {
                const { id, amount, finalAmount } = pay;
                if (remaining <= 0) {
                    newApplied[id] = 0;
                    newDeductibles[id] = amount;
                } else if (remaining >= finalAmount) {
                    newApplied[id] = finalAmount;
                    newDeductibles[id] = 0;
                    newSelected.push(id);
                    remaining -= finalAmount;
                } else {
                    const coverage = Math.min(remaining, amount);
                    newApplied[id] = coverage;
                    newDeductibles[id] = amount - coverage;
                    if (coverage > 0) newSelected.push(id);
                    remaining -= coverage;
                }
            }

            setDeductibles(newDeductibles);
            setAppliedAmounts(newApplied);
            setSelectedPayments(newSelected);
        } else if (
            customerData?.type === 'A' &&
            paymentOption === 'partial' &&
            !partialPaymentAmount
        ) {
            setSelectedPayments([]);
            setDeductibles({});
            setAppliedAmounts({});
        }
    }, [partialPaymentAmount, paymentOption, customerData?.type, payments]);

    // ----------------------------------------------------------------------
    // 4) MODO “M” (Manual) con enfoque “progresivo”
    // ----------------------------------------------------------------------
    // A) Suma total de abonos ya asignados
    const sumOfApplied = () => {
        return Object.values(appliedAmounts).reduce((a, b) => a + b, 0);
    };

    // B) Efecto cuando cambia partialPaymentAmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (
            customerData?.type === 'M' &&
            paymentOption === 'partial' &&
            payments.length > 0
        ) {
            const totalAbono = calculateAbonoAplicado();
            let sum = sumOfApplied();

            const newApplied = { ...appliedAmounts };
            const newDeductibles = { ...deductibles };

            if (sum > totalAbono) {
                let toRemove = sum - totalAbono;
                for (let i = selectedPayments.length - 1; i >= 0 && toRemove > 0; i--) {
                    const pid = selectedPayments[i];
                    const oldCoverage = newApplied[pid] || 0;
                    if (oldCoverage <= 0) continue;
                    const pay = payments.find((p) => p.id === pid);
                    if (!pay) continue;

                    if (oldCoverage <= toRemove) {
                        newApplied[pid] = 0;
                        newDeductibles[pid] = pay.amount;
                        toRemove -= oldCoverage;
                    } else {
                        newApplied[pid] = oldCoverage - toRemove;
                        newDeductibles[pid] = pay.amount - newApplied[pid];
                        toRemove = 0;
                    }
                }
            }
            setAppliedAmounts(newApplied);
            setDeductibles(newDeductibles);
        }
    }, [partialPaymentAmount]);

    // C) Efecto cuando se cambia la lista de facturas seleccionadas
    useEffect(() => {
        if (
            customerData?.type === 'M' &&
            paymentOption === 'partial' &&
            payments.length > 0
        ) {
            let newApplied = { ...appliedAmounts };
            let newDeductibles = { ...deductibles };
            const totalAbono = calculateAbonoAplicado();
            let sum = sumOfApplied();

            // 1) Liberar abono de facturas no seleccionadas
            payments.forEach((pay) => {
                if (!selectedPayments.includes(pay.id)) {
                    if (newApplied[pay.id] > 0) {
                        sum -= newApplied[pay.id];
                    }
                    newApplied[pay.id] = 0;
                    newDeductibles[pay.id] = pay.amount;
                }
            });

            // 2) Para facturas seleccionadas, si no tenían abono, asignar del sobrante
            for (let i = 0; i < selectedPayments.length; i++) {
                const pid = selectedPayments[i];
                const pay = payments.find((p) => p.id === pid);
                if (!pay) continue;

                if (newApplied[pid] === undefined) {
                    newApplied[pid] = 0;
                    newDeductibles[pid] = pay.amount;
                }

                let leftover = totalAbono - sum;
                if (leftover <= 0) continue;
                const { amount, finalAmount } = pay;
                let needed = finalAmount - newApplied[pid];
                if (needed <= 0) continue;
                let coverage = Math.min(leftover, needed);
                newApplied[pid] += coverage;
                sum += coverage;

                if (newApplied[pid] >= finalAmount) {
                    newDeductibles[pid] = 0;
                } else {
                    newDeductibles[pid] = amount - newApplied[pid];
                }
            }

            setAppliedAmounts(newApplied);
            setDeductibles(newDeductibles);
        }
    }, [selectedPayments]);

    // ----------------------------------------------------------------------
    // 5) Manejo de checkboxes
    // ----------------------------------------------------------------------
    const handleCheckboxChange = (paymentId) => {
        // Modo A => no check manual
        if (paymentOption === 'partial' && customerData?.type === 'A') return;

        // Modo M => requiere abono > 0 antes de marcar
        if (
            paymentOption === 'partial' &&
            customerData?.type === 'M' &&
            (!partialPaymentAmount || parseFloat(partialPaymentAmount) === 0)
        ) {
            setError('Debes ingresar un monto antes de seleccionar facturas.');
            showNotification('Debes ingresar un monto para abonar.', 'error');
            return;
        }

        setError('');
        const isSelected = selectedPayments.includes(paymentId);

        // Modo M => toggle
        if (paymentOption === 'partial' && customerData?.type === 'M') {
            const updated = isSelected
                ? selectedPayments.filter((id) => id !== paymentId)
                : [...selectedPayments, paymentId];
            setSelectedPayments(updated);
            return;
        }

        // Modo complete => check orden cronológico
        if (isSelected) {
            const lastSelectedId = selectedPayments[selectedPayments.length - 1];
            if (paymentId !== lastSelectedId) {
                setError(
                    'Debes deseleccionar las facturas en orden inverso (de la más reciente a la más antigua).'
                );
                showNotification('Deselecciona las facturas en orden inverso.', 'error');
                return;
            }
        } else {
            const lastIndex =
                selectedPayments.length > 0
                    ? payments.findIndex((p) => p.id === selectedPayments[selectedPayments.length - 1])
                    : -1;
            const currentIndex = payments.findIndex((p) => p.id === paymentId);
            if (currentIndex > lastIndex + 1) {
                setError(
                    'Debes seleccionar las facturas en orden cronológico (de la más antigua a la más reciente).'
                );
                showNotification('Selecciona las facturas en orden cronológico.', 'error');
                return;
            }
        }

        const updated = isSelected
            ? selectedPayments.filter((id) => id !== paymentId)
            : [...selectedPayments, paymentId];
        setSelectedPayments(updated);
    };

    // ----------------------------------------------------------------------
    // 6) Redirigir a Wompi (con ajuste en la referencia única)
    // ----------------------------------------------------------------------
    const redirectToWompi = async (selectedPayments, abono = 0, totalPagar = 0) => {
        try {

            const currentDateTime = new Date();
            const pad = (n) => String(n).padStart(2, '0');
            const dateFormatted = `${pad(currentDateTime.getDate())}-${pad(currentDateTime.getMonth() + 1)}-${currentDateTime.getFullYear()}`;
            const timeFormatted = `${pad(currentDateTime.getHours())}-${pad(currentDateTime.getMinutes())}`;
            const abonoSuffix = `-ABONO-${dateFormatted}_${timeFormatted}`;

            const invoicesList =
                selectedPayments.length === 1
                    ? (() => {
                        const paymentId = selectedPayments[0];
                        const pay = payments.find((p) => p.id === paymentId);
                        if (!pay) return '';
                        // Para una única factura:
                        // En modo "complete" se utiliza el monto total con descuento (finalAmount)
                        // En modo "partial" se usa el monto ingresado
                        const amountPaid =
                            paymentOption === 'complete'
                                ? pay.finalAmount
                                : calculateAbonoAplicado();
                        const discountValue =
                            paymentOption === 'complete'
                                ? pay.discount
                                : amountPaid >= pay.finalAmount
                                    ? pay.discount
                                    : 0;
                        const baseInvoice = `${pay.invoice}_${amountPaid}_DP-${discountValue}`;
                        // Si es modo abono, agregamos el sufijo a la factura
                        return paymentOption === 'partial' ? baseInvoice + abonoSuffix : baseInvoice;
                    })()
                    : selectedPayments
                        .map((paymentId, index) => {
                            const pay = payments.find((p) => p.id === paymentId);
                            if (!pay) return null;
                            let amountPaid;
                            // Para múltiples facturas, en modo "complete" se usa el total (finalAmount)
                            if (paymentOption === 'complete') {
                                amountPaid = pay.finalAmount;
                            } else {
                                // Usamos el valor ya asignado en appliedAmounts (sin sumar excedente a la última)
                                amountPaid = appliedAmounts[paymentId] || 0;
                            }
                            const discountValue =
                                paymentOption === 'complete'
                                    ? pay.discount
                                    : amountPaid >= pay.finalAmount
                                        ? pay.discount
                                        : 0;
                            const baseInvoice = `${pay.invoice}_${amountPaid}_DP-${discountValue}`;
                            // Agregamos el sufijo "ABONO" a cada factura si es abono
                            return paymentOption === 'partial' ? baseInvoice + abonoSuffix : baseInvoice;
                        })
                        .filter((x) => x !== null)
                        .join('-');

            let uniqueReference = `NIT-${customerData.id}-FAV-${invoicesList}`;
            const { cmpy, referencia, reg, teri, phone, evt, cash_received } = getExternalParams();
            // construir referencia extendida si existen parámetros externos
            if (cmpy && referencia) {
                uniqueReference =
                    `${uniqueReference}_APP_EVA_CMPY_${cmpy}_REG_${reg}_TERI_${teri}_PHONE_${phone}_REF_${referencia}_EVT_${evt}_CASHRECEIVED_${cash_received}`;
            }
            //const integritySecret = 'prod_integrity_L4gVACSljR0oGHMMKPvpKeZkDbqlMf2R';
            const currency = 'COP';
            const amountInCents = totalPagar * 100;
            const signatureString = `${uniqueReference}${amountInCents}${currency}${integritySecret}`;
            const encodedText = new TextEncoder().encode(signatureString);
            const hashBuffer = await window.crypto.subtle.digest('SHA-256', encodedText);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const signature = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

            let paymentStatus = 'Aprobado'

            const checkoutParams = {
                publicKey,
                currency,
                amountInCents,
                reference: uniqueReference,
                redirectUrl: `${window.location.origin}/confirmation`,
                //redirectUrl: `https://a1b2c3d4.ngrok.io/confirmation`,
                signature: { integrity: signature },
                customerData: {
                    email: customerData?.email,
                    fullName: customerData?.name,
                    phoneNumberPrefix: '+',
                    phoneNumber: customerData?.phone && customerData.phone.trim() !== ''
                        ? customerData.phone.trim()
                        : '0000000000'
                },
                abono
            };

            if (window.WidgetCheckout) {
                const checkout = new window.WidgetCheckout(checkoutParams);
                checkout.open((result) => {

                    if (
                        result.transaction?.status === 'APPROVED' ||
                        result.transaction?.status === 'success'
                    ) {
                        let timeLeft = 2;
                        const interval = setInterval(() => {
                            timeLeft--;
                            showNotification(
                                `✅ Pago aprobado. Redirigiendo al Comercio en ${timeLeft} segundos...`,
                                'success'
                            );
                            if (timeLeft <= 0) {
                                clearInterval(interval);
                                //window.location.href = `${window.location.origin}/confirmation?status=${encodeURIComponent(paymentStatus)}&id=${encodeURIComponent(result.transaction?.id)}`;
                            }
                        }, 1000);
                    } else if (result.transaction?.status === 'PENDING') {
                        let timeLeft = 2;
                        const interval = setInterval(() => {
                            timeLeft--;
                            showNotification(
                                `El pago se encuentra en estado PENDIENTE. Redirigiendo al Comercio en ${timeLeft} segundos...`,
                                'info'
                            );
                            if (timeLeft <= 0) {
                                clearInterval(interval);
                                window.location.href = `${window.location.origin}/confirmation?status=PENDING&id=${encodeURIComponent(result.transaction?.id)}`;
                            }
                        }, 1000);
                    }
                    else {
                        showNotification(`Pago fallido o cancelado. Estado: ${result.status}`, 'error');
                    }
                });
            } else {
                setError('El widget de Wompi no se cargó correctamente. Por favor intenta nuevamente más tarde.');
                showNotification('Error al cargar el widget de Wompi', 'error');
            }
        } catch (error) {
            console.error('Error al abrir Wompi:', error);
            setError('Error inesperado al procesar el pago.');
            showNotification('Error inesperado al procesar el pago.', 'error');
        }
    };

    // ----------------------------------------------------------------------
    // 7) Botón principal de pago
    // ----------------------------------------------------------------------
    const handleGeneralPay = () => {
        if (selectedPayments.length === 0) {
            setError('Por favor selecciona al menos una factura para pagar.');
            showNotification('Selecciona al menos una factura', 'error');
            return;
        }
        if (paymentOption === 'partial') {
            const abono = calculateAbonoAplicado();
            redirectToWompi(selectedPayments, abono, abono);
        } else {
            const totalPagarComplete = calculateTotalPagarComplete();
            redirectToWompi(selectedPayments, 0, totalPagarComplete);
        }
    };

    // ----------------------------------------------------------------------
    // 8) Cambiar modo de pago
    // ----------------------------------------------------------------------
    const handlePaymentOptionChange = (option) => {
        setError('');
        setSelectedPayments([]);
        setDeductibles({});
        setAppliedAmounts({});

        if (option === 'partial') {
            setPartialPaymentAmount('');
        }
        setPaymentOption(option);
    };

    // ----------------------------------------------------------------------
    // RENDER
    // ----------------------------------------------------------------------
    return (
        <div className="min-h-screen flex items-center justify-center px-4 background-container">
        <div className="flex items-start justify-center px-4 bg-transparent w-full">

                {notification && (
                    <div
                        className={`fixed top-4 right-4 p-3 rounded-lg shadow-md text-white text-sm ${
                            notification.type === 'success'
                                ? 'bg-green-500'
                                : notification.type === 'info'
                                    ? 'bg-blue-500'
                                    : 'bg-red-500'
                        }`}
                    >
                        {notification.message}
                    </div>
                )}

            <div
                className={`w-full max-w-md md:max-w-md lg:max-w-lg
                rounded-[28px] shadow-xl overflow-hidden relative
                ${customerData ? 'bg-white' : 'bg-white/70 backdrop-blur-md'}
                `}
            >
                    <div className="p-8 space-y-6">
                        <div className="flex justify-center mb-2">
                            <img
                                src="/logo.svg"
                                alt="Logo"
                                className="h-40 w-full max-w-[420px] object-contain"
                            />
                        </div>
                        <div>
                            <label htmlFor="id" className="block text-gray-800 font-semibold mb-2">
                                Documento de identidad:
                            </label>

                            <input
                                type="text"
                                id="id"
                                value={id}
                                onChange={(e) => setId(e.target.value)}
                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-full text-sm outline-none focus:ring-2
                                focus:ring-gray-300 placeholder:font-semibold placeholder:text-gray-400"
                                placeholder="C.C. o NIT"
                                disabled={isIdLocked || isLoading}
                            />

                            {!isIdLocked && !isLoading && (
                                <button
                                    onClick={handleValidateCustomer}
                                    className="w-full bg-gray-900 text-white py-3 mt-3 rounded-full font-medium hover:bg-gray-800 text-sm"
                                >
                                    Continuar
                                </button>
                            )}
                        </div>

                        {isLoading && (
                            <div className="flex justify-center items-center">
                                <ClipLoader color="#111827" size={35} />
                            </div>
                        )}

                        {error && <p className="text-red-500 text-xs text-center">{error}</p>}

                        {customerData && (
                            <div className="bg-gray-50 border border-gray-200 p-3 rounded-2xl">
                                <h2 className="text-center text-sm font-bold text-gray-900">
                                    Hola, {customerData.name}
                                </h2>
                                {payments.length > 0 && (
                                    <p className="text-center text-xs text-gray-600">
                                        Selecciona la factura que quieres pagar:
                                    </p>
                                )}
                            </div>
                        )}

                        {payments.length > 0 && (
                            <div
                                className="bg-gray-50 border border-gray-200 rounded-2xl p-3"
                                style={{ maxHeight: "400px", overflowY: "auto" }}
                            >
                                <div className="flex justify-center gap-4 mb-5">
                                    <button
                                        className={`h-12 px-8 rounded-xl font-semibold transition ${
                                            paymentOption === "complete"
                                                ? "bg-black text-white"
                                                : "bg-gray-300 text-gray-600"
                                        }`}
                                        onClick={() => handlePaymentOptionChange("complete")}
                                    >
                                        Pago Completo Factura
                                    </button>

                                    <button
                                        className={`h-12 px-6 rounded-xl font-semibold transition ${
                                            paymentOption === "partial"
                                                ? "bg-black text-white"
                                                : "bg-gray-300 text-gray-600"
                                        }`}
                                        onClick={() => handlePaymentOptionChange("partial")}
                                    >
                                        Abono
                                    </button>
                                </div>

                                {paymentOption === "partial" && (
                                    <>
                                        <p className="text-center text-red-500 text-base mb-2">
                                            Para aprovechar el beneficio del descuento por pronto pago, debe
                                            pagar el 100% del valor de la factura.
                                        </p>

                                        <div className="mb-5">
                                            <label className="block text-gray-800 text-base font-semibold mb-2">
                                                Monto para abonar:
                                            </label>

                                            <NumericFormat
                                                value={partialPaymentAmount}
                                                onValueChange={({ value }) => setPartialPaymentAmount(value)}
                                                thousandSeparator="."
                                                decimalSeparator=","
                                                prefix="$ "
                                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-full text-sm outline-none focus:ring-2 focus:ring-gray-300"
                                                placeholder="Ingrese el monto"
                                            />
                                        </div>
                                    </>
                                )}

                                {payments.map((payment) => {
                                    const {
                                        id,
                                        invoice,
                                        invoiceValue,
                                        amount,
                                        discount,
                                        finalAmount,
                                        date,
                                        miscInfo,
                                    } = payment;

                                    const abonoReal = appliedAmounts[id] ?? 0;

                                    let discountToShow = discount;
                                    let finalAmountToShow = finalAmount;
                                    if (paymentOption === "partial") {
                                        if (abonoReal < finalAmount) {
                                            discountToShow = 0;
                                            finalAmountToShow = amount;
                                        }
                                    }

                                    let pendienteUI = finalAmountToShow - abonoReal;
                                    if (pendienteUI < 0) pendienteUI = 0;

                                    let completionPercent = (abonoReal / finalAmountToShow) * 100;
                                    if (completionPercent > 100) completionPercent = 100;
                                    completionPercent = completionPercent.toFixed(2);

                                    const isChecked = selectedPayments.includes(id);

                                    return (
                                        <div
                                            key={id}
                                            className="bg-white border border-gray-200 rounded-2xl p-3 mb-4 shadow-sm flex flex-col"
                                        >
                                            <div className="flex items-center justify-between">
                                                {paymentOption === "partial" && customerData?.type === "A" ? (
                                                    isChecked ? (
                                                        <i className="fas fa-check-square text-gray-900 text-xl"></i>
                                                    ) : (
                                                        <i className="far fa-square text-gray-400 text-xl"></i>
                                                    )
                                                ) : (
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => handleCheckboxChange(id)}
                                                        className="h-5 w-5 rounded focus:ring-gray-400 focus:ring-2"
                                                    />
                                                )}

                                                <div className="ml-3 flex-1">
                                                    <p className="text-gray-900 font-semibold text-sm">
                                                        {miscInfo === "DB" ? "Nota Debito" : "Factura"}: {invoice}
                                                    </p>
                                                    <p className="text-gray-600 text-sm">
                                                        Valor Factura: ${invoiceValue.toLocaleString("es-CO")}
                                                    </p>
                                                    <p className="text-gray-600 text-sm">
                                                        Saldo Factura: ${amount.toLocaleString("es-CO")}
                                                    </p>
                                                    <p className="text-gray-600 text-sm">
                                                        Descuento por Pronto Pago: $
                                                        {discountToShow.toLocaleString("es-CO")}
                                                    </p>
                                                    <p className="text-gray-600 text-sm">
                                                        Valor Total con Descuento: $
                                                        {finalAmountToShow.toLocaleString("es-CO")}
                                                    </p>
                                                    <p className="text-gray-600 text-sm">
                                                        Fecha de Emisión: {new Date(date).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>

                                            {paymentOption === "partial" && isChecked && partialPaymentAmount && (
                                                <div className="mt-3 text-right flex flex-col items-center space-y-2">
                                                    <div>
                                                        <label className="block text-gray-600 text-sm">
                                                            Saldo Pendiente:
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={pendienteUI.toLocaleString("es-CO")}
                                                            readOnly
                                                            className="w-28 px-3 py-2 bg-gray-50 border border-gray-200 rounded-full text-sm"
                                                        />
                                                    </div>
                                                    <div
                                                        className={`text-xs font-semibold ${
                                                            completionPercent === "100.00"
                                                                ? "text-green-600"
                                                                : "text-red-500"
                                                        }`}
                                                    >
                                                        {`Cumplimiento: ${completionPercent}%`}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {paymentOption === "partial" && (
                                    <div className="mt-4 max-w-md mx-auto">
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                <span className="text-sm font-bold text-gray-900">
                                    Monto Abonado:
                                </span>
                                            <span className="text-sm font-bold text-gray-900 text-right">
                                    ${calculateAbonoAplicado().toLocaleString("es-CO")}
                                </span>

                                            <span className="text-sm font-bold text-gray-900">
                                    Saldo Pendiente:
                                </span>
                                            <span className="text-sm font-bold text-gray-900 text-right">
                                    ${calculatePendingBalanceOnlyForDisplay().toLocaleString("es-CO")}
                                </span>

                                            {calculateExcess() > 0 && (
                                                <>
                                        <span className="text-sm font-bold text-gray-900">
                                            Saldo a Favor:
                                        </span>
                                                    <span className="text-sm font-bold text-gray-900 text-right">
                                            ${calculateExcess().toLocaleString("es-CO")}
                                        </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {paymentOption === "complete" && (
                                    <div className="mt-2 text-sm font-bold text-gray-900 text-center">
                                        Total a Pagar: ${calculateTotalPagarComplete().toLocaleString("es-CO")}
                                    </div>
                                )}

                                <button
                                    onClick={handleGeneralPay}
                                    className="w-full bg-gray-900 text-white py-3 rounded-full font-medium hover:bg-gray-800 transition text-sm mt-5 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                    disabled={selectedPayments.length === 0}
                                >
                                    Pagar Seleccionados
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

        </div>
    );
};

export default WompiInterface;
